import { Injectable } from '@angular/core';
import { LazyCache } from '@herrenknecht-connected/shared-utils';
import { WEEK_DEFINITIONS, WeekDefinition, WeekDefinitionType } from '@herrenknecht-connected/shared-util-idm';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment-timezone';
import { combineLatest, Observable, ReplaySubject, startWith, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import _ from 'lodash-es';

export enum SupportedLanguages {
  German = 'de',
  English = 'en',
  Spanish = 'es',
  French = 'fr',
  Italian = 'it',
  Japanese = 'ja',
  Korean = 'ko',
  Polish = 'pl',
  Portuguese = 'pt',
  Romanian = 'ro',
  Chinese = 'zh',
}

// noinspection JSUnusedLocalSymbols
const __AzureLanguageCodeLookup = {
  [SupportedLanguages.German]: 'de-DE',
  [SupportedLanguages.English]: 'en-US',
  [SupportedLanguages.French]: 'fr-FR',
  [SupportedLanguages.Spanish]: 'es-ES',
  [SupportedLanguages.Chinese]: 'zh-CN',
  [SupportedLanguages.Polish]: 'pl-PL',
  [SupportedLanguages.Portuguese]: 'pt-PT',
  [SupportedLanguages.Italian]: 'it-IT',
  [SupportedLanguages.Korean]: 'ko-KR',
  [SupportedLanguages.Romanian]: 'ro-RO',
  [SupportedLanguages.Japanese]: 'ja-JA',
};

const MomentLanguageCodesLookup = {
  [SupportedLanguages.German]: 'de',
  [SupportedLanguages.English]: 'en',
  [SupportedLanguages.Chinese]: 'zh-cn',
  [SupportedLanguages.Polish]: 'pl',
  [SupportedLanguages.Portuguese]: 'pt',
  [SupportedLanguages.French]: 'fr',
  [SupportedLanguages.Spanish]: 'es',
  [SupportedLanguages.Italian]: 'it',
  [SupportedLanguages.Korean]: 'ko',
  [SupportedLanguages.Romanian]: 'ro',
  [SupportedLanguages.Japanese]: 'ja',
};

@Injectable({
  providedIn: 'root',
})
export class LocalizationService {
  protected _cache = new LazyCache();
  private _weekDef?: WeekDefinition;

  private _weekDefSubject: ReplaySubject<WeekDefinition> = new ReplaySubject<WeekDefinition>(1);
  private _momentLocaleChange: Subject<string> = new Subject<string>();

  constructor(public translate: TranslateService) {
    this.setNewWeekDefByType(WeekDefinitionType.iso);

    // subscribe to moment locale changes (language and week def)
    combineLatest([
      this.translate.onLangChange.pipe(
        map((event_) => event_.lang),
        startWith(this.translate.currentLang),
      ),
      this.weekDefChange,
    ]).subscribe(([lang, weekDefinition]) => {
      moment.updateLocale(this.momentLocalizationCode, { week: weekDefinition.definition });
      this._momentLocaleChange.next(lang);
    });
  }

  public get momentLocalizationCode() {
    return (
      MomentLanguageCodesLookup[this.translate.currentLang as SupportedLanguages] ?? MomentLanguageCodesLookup[SupportedLanguages.English]
    );
  }

  public get azureLocalizationCode() {
    return this.langToAzureLocalizationCode(this.translate.currentLang);
  }

  public get azureLocalizationCode$() {
    return this.translate.onLangChange.pipe(map((langEvent) => this.langToAzureLocalizationCode(langEvent.lang)));
  }

  public get weekDefType() {
    return this._weekDef?.type;
  }

  public get weekDefsAsArray() {
    return _.values(WEEK_DEFINITIONS);
  }

  public get weekDefChange(): Observable<WeekDefinition> {
    return this._weekDefSubject.asObservable();
  }

  static getFlagAbbreviation(lang: string): string {
    switch (lang) {
      case 'en': {
        return 'gb';
      }
      case 'zh': {
        return 'cn';
      }
      case 'ko': {
        return 'kr';
      }
      case 'ja': {
        return 'jp';
      }
      default: {
        return lang;
      }
    }
  }

  /**
   * Lookes up the values of the injection object and injects them into the key translate lookup
   *
   * @param key
   * @param injections
   */
  public getInjectedTranslation(key: string, injections: Record<string, string>) {
    const filledInjections = _.mapValues(injections, (value) => this.translate.instant(value));
    return this.translate.get(key, filledInjections);
  }

  public isLangValid(lang?: string): boolean {
    if (!lang) {
      return false;
    }
    return _.values(SupportedLanguages).includes(lang as SupportedLanguages);
  }

  public isWeekDefTypeValid(t: WeekDefinitionType): boolean {
    if (!t) {
      return false;
    }
    return _.values(WeekDefinitionType).includes(t);
  }

  public setNewWeekDefByType(t: WeekDefinitionType): void {
    if (this.isWeekDefTypeValid(t)) {
      this._weekDef = WEEK_DEFINITIONS[t];
      this._weekDefSubject.next(this._weekDef);
    }
  }

  private langToAzureLocalizationCode(lang: string) {
    return MomentLanguageCodesLookup[lang as SupportedLanguages] ?? MomentLanguageCodesLookup[SupportedLanguages.English];
  }
}
